import React, { useContext } from 'react';
import { MobXProviderContext } from 'mobx-react';
import { useHistory } from 'react-router-dom';

import Text from './Text';

/**
 * @typedef {TextProps} LinkProps
 * @property {string} to
 * @property {object} [state] - Optional state to pass with the navigation action
 * @property {boolean} [disabled]
 * @property {boolean} [blank] - If true, the link will open in a new tab
 * @property {function} [onClick]
 * @property {boolean} [keepSearch] - If true, the current URL's search parameters will be appended to the `to` URL
 */

/**
 * @param {LinkProps} props
 * @returns {React.JSX.Element}
 */
function Link({ to, state, disabled, blank, children, onClick, keepSearch, ...rest }) {
  const { $app, $auth } = useContext(MobXProviderContext);
  const history = useHistory();

  if ($app.isExport && $auth.hasPermission('suppressLinksInExports')) {
    return children || to;
  }

  if (history?.location?.search && keepSearch) {
    to += history.location.search;
  }

  const onClickWrapper = (e) => {
    e.stopPropagation();

    if (e.button === 0 && !(e.metaKey || e.altKey || e.ctrlKey || e.shiftKey) && !blank && !disabled) {
      e.preventDefault();

      if (onClick) {
        if (onClick.then) {
          return onClick().then(() => history.push(to, state));
        }
        onClick();
      }

      history.push(to, state);
      return false;
    }

    return true;
  };

  let target;
  if (blank) {
    target = '_blank';
  }

  return (
    <Text
      as="a"
      role="link"
      href={disabled || $app.isSharedLink ? undefined : to}
      onClick={$app.isSharedLink ? undefined : onClickWrapper}
      disabled={disabled}
      target={target}
      {...rest}
    >
      {children !== undefined ? children : to}
    </Text>
  );
}

export default Link;
